<template>
  <div class="pa-3">
    <!-- TOP BAR: Search + Status Filter + Back Button (inline on medium+) -->
    <v-row class="mb-4 align-center justify-space-between">
      <!-- Search field (flexible) -->
      <v-col cols="12" md="5" lg="4">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by Reference, Tenant or Property"
          outlined
          dense
          clearable
          class="rounded-lg small-text-field"
          hide-details
        />
      </v-col>

      <!-- Status filter buttons (inline, spaced evenly) -->
      <v-col cols="12" md="5" lg="4">
        <div class="d-flex justify-space-between align-center status-toggle-group">
          <v-btn
            :color="statusFilter === 'active' ? 'success' : 'default'"
            :outlined="statusFilter !== 'active'"
            small
            @click="statusFilter = 'active'"
            class="filter-btn"
          >
            <v-icon left small>mdi-check-circle</v-icon> Active
          </v-btn>
          <v-btn
            :color="statusFilter === 'cancelled' ? 'error' : 'default'"
            :outlined="statusFilter !== 'cancelled'"
            small
            @click="statusFilter = 'cancelled'"
            class="filter-btn"
          >
            <v-icon left small>mdi-cancel</v-icon> Cancelled
          </v-btn>
          <v-btn
            :color="statusFilter === 'all' ? 'primary' : 'default'"
            :outlined="statusFilter !== 'all'"
            small
            @click="statusFilter = 'all'"
            class="filter-btn"
          >
            <v-icon left small>mdi-view-list</v-icon> All
          </v-btn>
        </div>
      </v-col>

      <!-- Back button (right aligned) -->
      <v-col cols="12" md="2" class="text-right">
        <v-btn @click="$router.push('/dashboard')" class="back-btn" small>
          ← Back
        </v-btn>
      </v-col>
    </v-row>

    <!-- FULL WIDTH LEASES TABLE -->
    <v-data-table
      :headers="headers"
      :items="filteredLeases"
      :search="search"
      :items-per-page="15"
      class="elevation-2 compact-table"
      style="border-radius: 12px; overflow: hidden;"
      :expanded.sync="expanded"
      show-expand
      :loading="loadingLeases"
      :row-class="getRowClass"
      dense
    >
      <!-- Expand chevron — clickable look -->
      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <v-btn
          icon
          small
          class="expand-btn"
          :class="{ 'expand-btn-open': isExpanded }"
          @click="expand(!isExpanded)"
        >
          <v-icon small>mdi-chevron-down</v-icon>
        </v-btn>
      </template>

      <!-- Rental Amount -->
      <template v-slot:item.rental="{ item }">
        <span class="font-weight-medium black--text">{{ item.rental }}</span>
      </template>

      <!-- Balance Column with Spinner -->
      <template v-slot:item.balance="{ item }">
        <div v-if="item.balanceLoading" class="d-flex align-center">
          <v-progress-circular indeterminate size="16" width="2" color="primary" class="mr-1" />
          <span class="black--text">Loading...</span>
        </div>
        <span v-else :class="{ 'negative-balance': item.balanceValue < 0 }" class="black--text">
          {{ formatCurrency(item.balanceValue) }}
        </span>
      </template>

      <!-- Term with Expired Red -->
      <template v-slot:item.term="{ item }">
        <span :class="{ 'expired-lease': isLeaseExpired(item.endDate) && item.status !== 'cancelled' }" class="black--text">
          {{ item.term }}
        </span>
      </template>

      <!-- Unit Display -->
      <template v-slot:item.unit="{ item }">
        <span class="black--text">{{ item.unit || '—' }}</span>
      </template>

      <!-- Status badge -->
      <template v-slot:item.status="{ item }">
        <v-chip :color="item.status === 'active' ? 'success' : 'error'" dark small>
          {{ item.status?.toUpperCase() || 'ACTIVE' }}
        </v-chip>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center" style="gap: 4px;">
          <v-btn
            v-if="item.status === 'active'"
            icon
            small
            @click="cancelLease(item)"
            class="action-btn"
            title="Cancel Lease"
          >
            <v-icon small class="action-icon-red">mdi-cancel</v-icon>
          </v-btn>
          <v-btn icon small @click="editLease(item)" class="action-btn" title="Edit Lease">
            <v-icon small class="action-icon-grey">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small @click="confirmDelete(item)" class="action-btn" title="Delete Lease">
            <v-icon small class="action-icon-red">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- EXPANDED ROW -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-3 expanded-row-cell">
          <v-row dense>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Deposit Held:</strong> <span class="black--text">{{ item.depositHeld }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Deposit Required:</strong> <span class="black--text">{{ item.depositRequired }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Invoice Date:</strong> <span class="black--text">{{ item.invoiceDate }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Start Date:</strong> <span class="black--text">{{ item.startDate }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">End Date:</strong> <span class="black--text">{{ item.endDate }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Prorata Amount:</strong> <span class="black--text">{{ item.prorataAmount }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Rent Due Frequency:</strong> <span class="black--text">{{ item.rentDueFrequency }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Due Day:</strong> <span class="black--text">{{ item.dueDay }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Lease Type:</strong> <span class="black--text">{{ item.leaseType }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="black--text">Status:</strong>
              <v-chip :color="item.status === 'active' ? 'success' : 'error'" dark small>
                {{ item.status?.toUpperCase() || 'ACTIVE' }}
              </v-chip>
            </v-col>
            <v-col cols="12" class="py-1"><strong class="black--text">Notes:</strong> <span class="black--text">{{ item.notes || '—' }}</span></v-col>
          </v-row>

          <v-row dense class="mt-2">
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToFinances(item)">
                <v-icon left small>mdi-currency-zar</v-icon> Finances
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToAttachments(item)">
                <v-icon left small>mdi-folder-multiple</v-icon> Attachments
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToNotes(item)">
                <v-icon left small>mdi-note-text</v-icon> Notes
              </v-btn>
            </v-col>
            <v-col cols="12" md="3">
              <v-btn block class="golden-btn" small @click="goToReports(item)">
                <v-icon left small>mdi-chart-bar</v-icon> Reports
              </v-btn>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>

    <!-- FLOATING ADD LEASE BUTTON -->
    <v-btn @click="$router.push('/Addlease')" fab small fixed bottom right class="save-btn" style="z-index: 10;">
      <v-icon color="white">mdi-plus</v-icon>
    </v-btn>

    <!-- DELETE CONFIRM DIALOG -->
    <v-dialog v-model="deleteDialog" max-width="450">
      <v-card class="pa-3">
        <v-card-title class="dialog-title-red">Delete Lease?</v-card-title>
        <v-card-text class="black--text">
          Are you sure you want to delete lease<br>
          <strong>{{ leaseToDelete?.leaseReference }}</strong> for<br>
          <strong>{{ leaseToDelete?.leaseName }}</strong> at<br>
          <strong>{{ leaseToDelete?.propertyName }}</strong>?<br><br>
          This cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark small @click="deleteLease">Delete Lease</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CANCEL CONFIRM DIALOG -->
    <v-dialog v-model="cancelDialog" max-width="450">
      <v-card class="pa-3">
        <v-card-title class="dialog-title-red">Cancel Lease?</v-card-title>
        <v-card-text class="black--text">
          Are you sure you want to cancel lease<br>
          <strong>{{ leaseToCancel?.leaseReference }}</strong> for<br>
          <strong>{{ leaseToCancel?.leaseName }}</strong> at<br>
          <strong>{{ leaseToCancel?.propertyName }}</strong>?<br><br>
          The lease status will be set to "cancelled".
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="cancelDialog = false">No, go back</v-btn>
          <v-btn color="red" dark small @click="confirmCancelLease">Yes, Cancel Lease</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- LOADING OVERLAY -->
    <v-overlay :value="loadingLeases">
      <v-progress-circular size="60" color="#f1c284" indeterminate />
    </v-overlay>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, deleteDoc, doc, updateDoc, query, where, writeBatch } from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";

const db = getFirestore(firebaseApp);

async function logEvent(action, target = "", type = "Lease Management") {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const event = {
    date: Math.floor(Date.now() / 1000),
    eventType: type,
    eventAction: action,
    user: currentUser.mobile || currentUser.email || "System",
    userName: `${currentUser.name || ""} ${currentUser.lastname || ""}`.trim(),
    targetEmail: target,
  };
  // await addDoc(collection(db, "ActivityLog"), event);
}

export default {
  data() {
    return {
      loadingLeases: true,
      search: "",
      expanded: [],
      deleteDialog: false,
      cancelDialog: false,
      leaseToDelete: null,
      leaseToCancel: null,
      leases: [],
      balances: {},
      statusFilter: "active",
      headers: [
        { text: "Lease Ref", value: "leaseReference", width: "120px" },
        { text: "Tenant", value: "tenantName" },
        { text: "Property", value: "propertyName" },
        { text: "Unit", value: "unit", width: "80px" },
        { text: "Term", value: "term" },
        { text: "Rental", value: "rental", align: "end" },
        { text: "Balance", value: "balance", align: "end", sortable: false },
        { text: "Status", value: "status", align: "center", width: "90px" },
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "130px" },
      ],
    };
  },

  computed: {
    leasesWithBalance() {
      return this.leases.map(lease => {
        const balanceInfo = this.balances[lease.id] || { loading: true, value: 0 };
        return {
          ...lease,
          balanceLoading: balanceInfo.loading,
          balanceValue: balanceInfo.value,
        };
      });
    },
    filteredLeases() {
      if (this.statusFilter === "all") return this.leasesWithBalance;
      return this.leasesWithBalance.filter(lease => lease.status === this.statusFilter);
    },
  },

  async mounted() {
    await this.fetchLeases();
  },

  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined) return 'R 0.00';
      return `R ${value.toFixed(2)}`;
    },

    getRowClass(item) {
      if (item.status === 'cancelled') return 'cancelled-lease-row';
      if (this.isLeaseExpired(item.endDate)) return 'expired-lease-row';
      return '';
    },

    async fetchLeases() {
      this.loadingLeases = true;
      try {
        const snapshot = await getDocs(collection(db, "Leases"));
        const leases = [];

        for (const docSnap of snapshot.docs) {
          const d = docSnap.data();
          const ld = d.leaseDetails || {};
          const isMtM = ld.type === "Month to Month Lease" || !ld.endDate;

          leases.push({
            id: docSnap.id,
            leaseReference: d.leaseReference || "—",
            tenantName: d.tenantName || "—",
            propertyName: d.propertyName || "—",
            unit: d.address?.includes("Unit") ? d.address.split("Unit")[1].trim() : "",
            term: isMtM ? "Month-to-Month" : `${ld.startDate || "—"} to ${ld.endDate || "—"}`,
            rental: `R ${Number(ld.rentalAmount || 0).toFixed(2)}`,
            depositHeld: `R ${Number(d.depositHeld || 0).toFixed(2)}`,
            depositRequired: `R ${Number(d.depositRequired || 0).toFixed(2)}`,
            invoiceDate: ld.invoiceDate || "1st of each Month",
            startDate: ld.startDate || "—",
            endDate: isMtM ? "Month-to-Month" : (ld.endDate || "—"),
            prorataAmount: `R ${Number(ld.prorataAmount || 0).toFixed(2)}`,
            rentDueFrequency: ld.rentDueFrequency || "Monthly",
            dueDay: ld.dueDay || "1st",
            leaseType: ld.type || "Fixed Term",
            status: d.status || "active",
            notes: d.notes || "",
            tenantId: d.tenantId,
            propertyId: d.propertyId,
            address: d.address || "",
          });
        }

        leases.sort((a, b) => {
          const numA = parseInt(a.leaseReference.replace(/\D/g, ""), 10) || 0;
          const numB = parseInt(b.leaseReference.replace(/\D/g, ""), 10) || 0;
          return numA - numB;
        });

        this.leases = leases;
        leases.forEach(lease => this.fetchBalance(lease.id, lease.leaseReference));
      } catch (error) {
        console.error("Error fetching leases:", error);
      } finally {
        this.loadingLeases = false;
      }
    },

    async fetchBalance(leaseId, leaseRef) {
      this.$set(this.balances, leaseId, { loading: true, value: 0 });
      try {
        const todayStr = new Date().toISOString().slice(0, 10);
        const transactionsSnap = await getDocs(
          query(
            collection(db, "Transactions"),
            where("leaseReference", "==", leaseRef),
            where("datePaid", "<=", todayStr)
          )
        );
        const balance = transactionsSnap.docs.reduce((sum, t) => {
          return sum + (parseFloat(t.data().amountPaid) || 0);
        }, 0);
        this.$set(this.balances, leaseId, { loading: false, value: balance });
      } catch (error) {
        console.error(`Error fetching balance for lease ${leaseRef}:`, error);
        this.$set(this.balances, leaseId, { loading: false, value: 0 });
      }
    },

    isLeaseExpired(endDate) {
      if (!endDate || endDate === "Month-to-Month") return false;
      return new Date(endDate) < new Date().setHours(0, 0, 0, 0);
    },

    editLease(item) {
      this.$router.push(`/EditLease/${item.leaseReference}`);
    },

    cancelLease(item) {
      this.leaseToCancel = item;
      this.cancelDialog = true;
    },

    async confirmCancelLease() {
      if (!this.leaseToCancel) return;
      try {
        const leaseRef = doc(db, "Leases", this.leaseToCancel.id);
        await updateDoc(leaseRef, { status: "cancelled" });
        await logEvent(
          "Lease Cancelled",
          `${this.leaseToCancel.tenantName} (${this.leaseToCancel.leaseReference})`,
          "Lease Management"
        );
        alert("Lease has been cancelled.");
        this.cancelDialog = false;
        this.leaseToCancel = null;
        await this.fetchLeases();
      } catch (error) {
        console.error("Error cancelling lease:", error);
        alert("Failed to cancel lease. Please try again.");
      }
    },

    goToFinances(item) {
      this.$router.push({
        path: "/finances",
        query: {
          leaseId: item.id,
          leaseReference: item.leaseReference,
          tenantName: item.tenantName,
          propertyName: item.propertyName,
        },
      });
    },

    goToAttachments(item) {
      this.$router.push({ name: "Attachments", params: { collection: "Leases", id: item.id } });
    },

    goToNotes(item) {
      this.$router.push({ name: "Notes", params: { id: item.id }, query: { collection: "Leases" } });
    },

    goToReports(item) {
      this.$router.push({ name: "LeaseReports", params: { id: item.id }, query: { collection: "lease" } });
    },

    confirmDelete(item) {
      this.leaseToDelete = item;
      this.deleteDialog = true;
    },

    async deleteLease() {
      if (!this.leaseToDelete) return;
      const { id, leaseReference, tenantId, propertyId, address } = this.leaseToDelete;

      try {
        await deleteDoc(doc(db, "Leases", id));

        if (tenantId) {
          const q = query(collection(db, "tenants"), where("reference", "==", tenantId));
          const snap = await getDocs(q);
          snap.forEach(d => updateDoc(d.ref, { placed: false }));
        }

        if (propertyId) {
          const unitsCol = collection(db, "properties", propertyId, "units");
          const unitSnap = await getDocs(unitsCol);
          if (!unitSnap.empty && address) {
            const uq = query(unitsCol, where("unitName", "==", address));
            const us = await getDocs(uq);
            us.forEach(u => updateDoc(u.ref, { isOccupied: false }));
          } else {
            await updateDoc(doc(db, "properties", propertyId), { isVacant: true });
          }
        }

        const transQ = query(collection(db, "Transactions"), where("leaseReference", "==", leaseReference));
        const transSnap = await getDocs(transQ);
        if (!transSnap.empty) {
          const batch = writeBatch(db);
          transSnap.forEach(d => batch.delete(d.ref));
          await batch.commit();
        }

        await logEvent(
          "Lease Deleted",
          `${this.leaseToDelete.tenantName} (${this.leaseToDelete.leaseReference})`,
          "Lease Management"
        );

        alert("Lease deleted successfully!");
        this.deleteDialog = false;
        await this.fetchLeases();
      } catch (error) {
        console.error("Delete failed:", error);
        alert("Failed to delete lease");
      }
    },
  },
};
</script>

<style>
/* === GLOBAL — styles for v-data-table rows (not reachable via scoped CSS) === */

tr.cancelled-lease-row > td {
  background-color: #f5f5f5 !important;
  opacity: 0.7;
  text-decoration: line-through;
  color: #888 !important;
}

tr.expired-lease-row > td {
  background-color: #fdecea !important;
  color: #b71c1c !important;
}
tr.expired-lease-row:hover > td {
  background-color: #fbd9d5 !important;
}

.v-data-table__expanded__content {
  background-color: #fff8e1 !important;
}
td.expanded-row-cell {
  background-color: #fff8e1 !important;
  border-top: 2px solid #f1c284 !important;
  border-bottom: 2px solid #f1c284 !important;
}

/* Expand chevron — obvious clickable gold circle */
.expand-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  width: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  min-height: 30px !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
  cursor: pointer !important;
  border: 1px solid #d4a35a !important;
}
.expand-btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25) !important;
  transform: scale(1.1);
}
.expand-btn .v-icon {
  color: #000000 !important;
  font-size: 20px !important;
  transition: transform 0.2s ease !important;
}
.expand-btn-open .v-icon {
  transform: rotate(180deg);
}

/* Action icons — force their colors past any black override */
.action-icon-red {
  color: #d32f2f !important;
}
.action-icon-grey {
  color: #616161 !important;
}
</style>

<style scoped>
/* --- Component-scoped styles --- */

.compact-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
  font-size: 0.875rem !important;
}

.compact-table :deep(.v-data-table-header th) {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 10px 12px !important;
  background-color: #f0f0f0 !important;
  color: #000000 !important;
  border-bottom: 2px solid #ccc !important;
}

.compact-table :deep(.v-data-table-header th span) {
  color: #000000 !important;
}

.compact-table :deep(.v-data-table-header th .v-data-table-header__icon) {
  color: #000000 !important;
}

.compact-table :deep(tbody td) {
  font-size: 0.875rem !important;
  padding: 8px 12px !important;
  color: #000000 !important;
}

/* Only default icons get forced to black — action icons keep their color */
.compact-table :deep(tbody td .v-icon:not(.action-icon-red):not(.action-icon-grey)) {
  color: #000000 !important;
}

.compact-table :deep(.v-data-table__expanded__content) {
  font-size: 0.875rem !important;
}

.black--text {
  color: #000000 !important;
}

.small-text-field :deep(.v-input__slot) {
  min-height: 40px !important;
}

.small-text-field :deep(.v-label) {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.875rem !important;
  color: #000000 !important;
}

.golden-btn,
.save-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  text-transform: none !important;
  font-size: 0.8rem !important;
  min-height: 34px !important;
}

.golden-btn .v-icon,
.save-btn .v-icon {
  color: #000000 !important;
}

.save-btn {
  border-radius: 50% !important;
  min-height: 44px !important;
  min-width: 44px !important;
}

.save-btn .v-icon {
  color: #ffffff !important;
}

.back-btn {
  background-color: #666 !important;
  color: #ffffff !important;
  border-radius: 20px !important;
  text-transform: none !important;
  font-size: 0.875rem !important;
  min-height: 36px !important;
  padding: 0 18px !important;
}

.back-btn .v-icon {
  color: #ffffff !important;
}

.status-toggle-group {
  gap: 8px;
}
.filter-btn {
  flex: 1;
  text-transform: none;
  font-weight: 500;
  border-radius: 20px !important;
  padding: 6px 12px;
  font-size: 0.8rem !important;
  min-height: 34px !important;
  letter-spacing: 0.3px;
  color: #000000 !important;
}

.filter-btn .v-icon {
  margin-right: 4px;
}

.filter-btn:not(.v-btn--active) {
  background-color: #f5f5f5 !important;
  color: #000000 !important;
}

.action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
}

.action-btn .v-icon {
  font-size: 18px !important;
}

.expired-lease {
  color: #d32f2f !important;
  font-weight: bold;
}

.negative-balance {
  color: #d32f2f !important;
}

/* Dialog titles — the .dialog-title-red class keeps its color */
.v-dialog .v-card-title {
  font-size: 1rem !important;
  font-weight: 700 !important;
  /* No color override — let the class on each title decide */
}

.dialog-title-red {
  color: #d32f2f !important;
}

.v-dialog .v-card-text {
  font-size: 0.875rem !important;
  /* No color override — .black--text on the paragraph handles it */
}

.v-dialog .v-btn {
  font-size: 0.8rem !important;
}

:deep(.v-chip) {
  font-size: 0.75rem !important;
  height: 24px !important;
}

@media (max-width: 768px) {
  .compact-table :deep(.v-data-table-header th) {
    font-size: 0.75rem !important;
    padding: 8px 8px !important;
  }

  .compact-table :deep(tbody td) {
    font-size: 0.8rem !important;
    padding: 6px 8px !important;
  }

  .status-toggle-group {
    gap: 6px;
  }
  .filter-btn {
    font-size: 0.7rem !important;
    padding: 4px 8px !important;
  }
  .filter-btn .v-icon {
    font-size: 14px !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>